/* eslint-disable space-before-function-paren */
/* eslint-disable no-throw-literal */
/* eslint-disable camelcase */
import axios, { CancelTokenSource } from 'axios';
import { isObject } from 'lodash';
import { onGetRefreshToken, onRefreshToken } from '~/api/authentication';
import { API_CONFIG, services } from '~/config/constants';
import { getConfig, setConfig } from '~/contexts/stores/configStore';
import { getAdminAccess } from '~/api/adminMfa';
import { setAdminMfaRequired, setAdminMfaSession } from '~/redux/modules/adminMfa';
import { authUser } from '~/redux/modules/authentication';
import { ProfileState } from '~/redux/modules/profile';
import { store } from '~/redux/store';
import { API_DATA, API_METHOD, ApiErrorBody, CONTENT_TYPE } from '~/types';
import { generateClientId, getClientId } from '~/utils/getClientId';
import { isEmbedded, isPublicPath } from '~/utils/helpers';
import { setPostLoginRedirect } from '~/utils/authRedirect';
import { establishSessionFromHost } from '~/utils/handoffSession';
import Storage from '~/utils/Storage';

// Request deduplication and caching system
interface CacheEntry {
    data: any;
    timestamp: number;
    promise: Promise<any>;
}

interface PendingRequest {
    promise: Promise<any>;
    timestamp: number;
}

class RequestCache {
    private cache = new Map<string, CacheEntry>();
    private pendingRequests = new Map<string, PendingRequest>();
    private readonly CACHE_DURATION = 1000; // 1 second cache duration

    private generateKey(endpoint: string, method: string, data?: any): string {
        const dataString = data ? JSON.stringify(data) : '';

        return `${method}:${endpoint}:${dataString}`;
    }

    private isExpired(timestamp: number): boolean {
        return Date.now() - timestamp > this.CACHE_DURATION;
    }

    get(endpoint: string, method: string, data?: any): any | null {
        const key = this.generateKey(endpoint, method, data);
        const entry = this.cache.get(key);

        if (entry && !this.isExpired(entry.timestamp)) {
            return entry.data;
        }

        if (entry) {
            this.cache.delete(key);
        }

        return null;
    }

    set(endpoint: string, method: string, data: any, responseData: any): void {
        const key = this.generateKey(endpoint, method, data);

        this.cache.set(key, {
            data: responseData,
            timestamp: Date.now(),
            promise: Promise.resolve(responseData)
        });
    }

    getPendingRequest(endpoint: string, method: string, data?: any): Promise<any> | null {
        const key = this.generateKey(endpoint, method, data);
        const pending = this.pendingRequests.get(key);

        if (pending && !this.isExpired(pending.timestamp)) {
            return pending.promise;
        }

        if (pending) {
            this.pendingRequests.delete(key);
        }

        return null;
    }

    setPendingRequest(endpoint: string, method: string, data: any, promise: Promise<any>): void {
        const key = this.generateKey(endpoint, method, data);

        this.pendingRequests.set(key, {
            promise,
            timestamp: Date.now()
        });
    }

    clearPendingRequest(endpoint: string, method: string, data?: any): void {
        const key = this.generateKey(endpoint, method, data);

        this.pendingRequests.delete(key);
    }

    clear(): void {
        this.cache.clear();
        this.pendingRequests.clear();
    }

    // Clean up expired entries periodically
    cleanup(): void {
        const now = Date.now();

        // Clean cache
        for (const [key, entry] of this.cache.entries()) {
            if (now - entry.timestamp > this.CACHE_DURATION) {
                this.cache.delete(key);
            }
        }

        // Clean pending requests
        for (const [key, pending] of this.pendingRequests.entries()) {
            if (now - pending.timestamp > this.CACHE_DURATION) {
                this.pendingRequests.delete(key);
            }
        }
    }
}

// Global cache instance
const requestCache = new RequestCache();

// let source: CancelTokenSource;

// Track all pending request cancel tokens
const pendingRequests = new Set<CancelTokenSource>();

// Clean up cache every 30 seconds
setInterval(() => {
    requestCache.cleanup();
}, 30000);

const { API_HOST, API_ID, API_TIME_OUT } = API_CONFIG;
const APP_JSON = 'application/json';
const APP_PDF = 'application/pdf';

// let source: CancelTokenSource;
// let currentEndpoint = '';

// let startTime = 0;

type Request = {
    url: string;
    method: API_METHOD;
    data?: API_DATA;
    headers: {
        Authorization?: string;
        'Content-Type': string;
        Accept: string;
        responseType?: string;
        secretKey?: string;
        key?: string; // for captcha requests
        secretToken?: string; // for elastic webhook
        unAuth?: string;
        customToken?: string;
        isPublic?: string;
    };
    onUploadProgress?: (progressEvent: any) => void;
    isPublic?: boolean;
};

type API_TYPE = {
    endpoint: string;
    method?: API_METHOD;
    data?: API_DATA;
    token?: string;
    unAuth?: boolean;
    isHeaderSecretKeys?: boolean;
    isSerialized?: boolean;
    contentType?: CONTENT_TYPE;
    onUploadProgress?: (progressEvent: any) => void;
    skipCache?: boolean; // Option to skip cache for specific requests
    isPublic?: boolean;
};

const API = axios.create({
    baseURL: `${API_HOST}/${API_ID}`,
    headers: {
        'Content-Type': APP_JSON,
        secretKey: getConfig().API_KEY!
    },
    timeout: API_TIME_OUT
});

/**
 * A 403 `admin_mfa_required` is not proof the admin session has ended.
 *
 * A session holds one token, so a tab that mints one — on a reload, or on first
 * opening — rotates away whatever another tab of the same browser was holding.
 * That tab's session is perfectly alive; only its copy of the token is stale,
 * and its next guarded request comes back 403.
 *
 * Concluding there would send that member to the code prompt for a session they
 * are still entitled to, which is the multi-tab bug wearing a different hat. So
 * ask `…/admin/access` first: if the session is live the server mints a
 * replacement and the caller replays, having noticed nothing. Only a server
 * that says the session is gone ends it.
 *
 * Single-flight on purpose. Several requests in flight will 403 together, and
 * one recovery call per failure would mint a token per failure — spending the
 * very budget whose exhaustion caused this.
 *
 * @returns {Promise<Boolean>} whether a usable token was recovered
 */
let adminMfaRecovery: Promise<boolean> | null = null;

async function recoverAdminMfaSession(): Promise<boolean> {
    const { id } = store.getState().profile || {};

    if (!id) return false;

    if (!adminMfaRecovery) {
        adminMfaRecovery = getAdminAccess(id)
            .then((response: any) => {
                const mfa = response?.mfa || {};

                // A valid session with no token means the server still
                // recognises the one we hold, so the 403 was not about eviction
                // and replaying would only fail again.
                if (!mfa.valid || !mfa.token) return false;

                setConfig({ ADMIN_MFA_TOKEN: mfa.token });
                store.dispatch(setAdminMfaSession(mfa));

                return true;
            })
            .catch(() => false)
            .finally(() => {
                adminMfaRecovery = null;
            });
    }

    return adminMfaRecovery;
}

API.interceptors.response.use(function (response) {
    // Remove the cancel token from pending requests when request completes
    const cancelTokenSource = (response.config as any)?.__cancelTokenSource;

    if (cancelTokenSource) {
        pendingRequests.delete(cancelTokenSource);
    }

    // console.log('endTime:', new Date().getTime());
    // function millisToMinutesAndSeconds(millis: number) {
    //     var minutes = Math.floor(millis / 60000);
    //     var seconds = ((millis % 60000) / 1000).toFixed(0);
    //     return (+seconds < 10 ? '0' : '') + seconds;
    // }
    // console.log('Seconds:', millisToMinutesAndSeconds(endTime - startTime));
    // console.log('--------------------------------');
    return response;
}, function (error) {
    // Remove the cancel token from pending requests when request fails
    const cancelTokenSource = (error.config as any)?.__cancelTokenSource;

    if (cancelTokenSource) {
        pendingRequests.delete(cancelTokenSource);
    }

    return Promise.reject(error);
});

const getHeaderValue = (headers: any, name: string): string | undefined => {
    if (!headers) return undefined;

    const key = Object.keys(headers).find(h => h.toLowerCase() === name.toLowerCase());

    return key ? headers[key] : undefined;
};

/**
 * Whether a rejection carries a structured API error body.
 *
 * @param {unknown} error the value a rejected request produced
 * @returns {Boolean} true when error_code is present
 */
export const isApiErrorBody = (error: unknown): error is ApiErrorBody =>
    typeof error === 'object' &&
    error !== null &&
    typeof (error as ApiErrorBody).error_code === 'string';

/**
 * The response body as a structured error, or null when it is not one.
 *
 * Retry-After is folded in from the header when the body does not carry it, so
 * a caller reading retry_after_seconds does not have to know which of the two
 * the server used.
 *
 * @param {any} response the axios response
 * @returns {ApiErrorBody|null} the body, or null
 */
const toApiErrorBody = (response: any): ApiErrorBody | null => {
    const body = response?.data;

    if (!body || typeof body !== 'object' || typeof body.error_code !== 'string') {
        return null;
    }

    const retryAfterHeader = getHeaderValue(response?.headers, 'Retry-After');
    const retryAfterFromHeader = retryAfterHeader ? parseInt(retryAfterHeader, 10) : NaN;

    return {
        ...body,
        http_status: response?.status,
        retry_after_seconds:
            body.retry_after_seconds ??
            (Number.isNaN(retryAfterFromHeader) ? undefined : retryAfterFromHeader)
    };
};

API.interceptors.response.use(
    (response: any) => response,
    async (error: { status?: any; message?: any; response?: any; config?: any }) => {
        // Don't process cancelled requests
        if (axios.isCancel(error)) {
            return Promise.reject(error);
        }

        const { response, config } = error;
        const data = response?.data?.status;
        // Don't stringify the raw axios error — it embeds config.data (the full
        // request body, e.g. a base64 PDF) and blows the localStorage quota.
        const apiError = JSON.stringify(
            data || {
                message: error?.message,
                status: response?.status,
                url: config?.url,
            }
        );

        Storage.setItem('apiError', apiError);

        // A structured body carries error_code, message and any remaining
        // budgets. Rejecting with `data` here would hand the caller
        // response.data.status, which for these is the literal string 'failed'.
        const structuredError = toApiErrorBody(response);

        // An expired admin session is not an expired app session: it must not
        // reach REFRESH_TOKEN below, which would try to repair the wrong thing.
        if (response?.status === 403 && structuredError?.error_code === 'admin_mfa_required') {
            const failedUrl: string = config?.url || '';

            // Never recover the recovery call, or a genuinely dead session
            // loops. The MFA endpoints answer for themselves.
            const isAdminMfaCall =
                failedUrl.includes('/admin/access') || failedUrl.includes('/admin/mfa/');

            if (!isAdminMfaCall && !config?.isAdminMfaRetry) {
                const isRecovered = await recoverAdminMfaSession();

                if (isRecovered) {
                    // Retried once and once only — a second 403 after a fresh
                    // token is the server meaning it.
                    config.isAdminMfaRetry = true;
                    config.headers['X-Admin-MFA-Token'] = getConfig().ADMIN_MFA_TOKEN;

                    return await API(config);
                }
            }

            setConfig({ ADMIN_MFA_TOKEN: undefined });
            store.dispatch(setAdminMfaRequired());

            return Promise.reject(structuredError);
        }

        if (error.status !== 401 && !apiError.includes('Signature has expired')) {
            if (structuredError) {
                return Promise.reject(structuredError);
            }

            if (data) {
                return Promise.reject(data);
            }

            return Promise.reject(
                error?.message === 'timeout of 15000ms exceeded'
                    ? new Error('Network Error')
                    : error
            );
        }

        // If the failing request IS a token/refresh or login call, don't try to
        // refresh again — that recurses into this same interceptor forever and
        // hangs the app (infinite spinner on an expired refresh token). Reject
        // so the caller (e.g. useGetRefreshToken) can log the user out instead.
        const requestUrl: string = config?.url || '';

        if (requestUrl.includes('/token/refresh-token') || requestUrl.includes('/login')) {
            return Promise.reject(data || error);
        }

        console.log('Expired token', error);

        return REFRESH_TOKEN(config)
            .then(resp => {
                console.log('Refreshed token', resp);

                return resp;
            })
            .catch(err => {
                return Promise.reject(err);
            });
    }
);

API.interceptors.request.use(async config => {
    const env = getConfig();

    // No API APP ID in public requests
    if (config?.headers?.isPublic === 'true' || config?.url?.includes(services?.welfareService)) {
        if (config?.headers?.isPublic) {
            delete config.headers.isPublic;
        }

        // Remove API ID from public requests
        config.baseURL = API_HOST;
    }

    const isPublic = isPublicPath(config.url);

    const secretKey = isPublic ? env.API_PUBLIC_KEY : env.API_KEY;
    const clientId = getClientId();

    if (config.headers) {
        config.headers.secretKey = secretKey;
        config.headers['x-client-id'] = clientId;

        if (config.headers.unAuth === 'true') {
            delete config.headers.unAuth;
            delete config.headers.Authorization;

            return config;
        }

        config.headers.Authorization = `Bearer ${config.headers.customToken || env.TOKEN}`;

        // A separate credential from the auth JWT above: this one exists only
        // because the member passed an email challenge, and is revocable
        // without touching authentication. Held in memory (configStore), so it
        // is absent after a reload until GET admin/access re-mints it.
        //
        // Deliberately below the unAuth early-return and behind !isPublic. This
        // app serves 35 unauthenticated /public/ endpoints — the member-facing
        // pathway — and none of them reads an admin session. Sending it there
        // would put a bearer credential on requests that are specifically meant
        // to carry none, and an unAuth request has just had its Authorization
        // stripped for exactly that reason.
        if (!isPublic && env.ADMIN_MFA_TOKEN) {
            config.headers['X-Admin-MFA-Token'] = env.ADMIN_MFA_TOKEN;
        }

        // Embedded in the Military App's iframe or the mobile WebView, none of
        // this is the right response to a missing token. There is a host that
        // can mint a new session without troubling the user, so the request is
        // allowed to go out, fail with 401, and recover through the handoff
        // path in REFRESH_TOKEN below.
        //
        // Every line of the block would actively harm that recovery:
        // generateClientId rotates the id the session is bound to, invalidating
        // the token a handoff is about to produce; cancelAllRequests kills the
        // exchange itself if one is in flight; and the redirect replaces the
        // iframe with a password form for a user who is already signed in to
        // the page framing it.
        if (!isPublic && !config.headers.customToken && !env.TOKEN && !isEmbedded()) {
            Storage.removeItem(env.REACT_APP_FIRESTORE_DB_ENV_VERSION_NAME);
            generateClientId();
            cancelAllRequests('No token available');
            // Remember the current route so login can return the user here.
            setPostLoginRedirect(window.location.pathname + window.location.search);
            window.location.href = '/login' + window.location.search;
        }

        delete config.headers.customToken;
    }

    return config;
}
);

/**
 * Cancels all pending axios requests
 * @param {String} [reason='Cancelled by user'] - Optional cancellation reason
 */
export const cancelAllRequests = (reason: string = 'Cancelled by user') => {
    pendingRequests.forEach((cancelTokenSource) => {
        try {
            cancelTokenSource.cancel(reason);
        } catch (error) {
            // Token may already be cancelled or request completed
            console.warn('Error cancelling request:', error);
        }
    });
    pendingRequests.clear();
};

const serialize = (data: Object) => {
    return Object.keys(data)
        .map((k, i) => {
            const value = encodeURIComponent(Object.values(data)[i]);
            const key = encodeURIComponent(k);

            return `${key}=${value || ''}`;
        })
        .join('&');
};

export default async function API_REQUEST({
    endpoint,
    method = 'GET',
    data,
    token,
    unAuth,
    isHeaderSecretKeys,
    isSerialized,
    contentType = APP_JSON,
    onUploadProgress,
    skipCache = false,
    isPublic = false
}: API_TYPE) {
    // currentEndpoint = endpoint;
    const { group }: ProfileState = store.getState().profile;

    const isConvertToPdf = endpoint.includes('convert-to-pdf');

    const formattedData = data ? (isSerialized ? serialize(data) : data) : '';

    const selectedPrimaryGroup = group?.id;

    let selectedPrimaryGroupQuery = '';

    if (selectedPrimaryGroup) {
        selectedPrimaryGroupQuery = endpoint?.includes('?')
            ? `&selected_primary_group_id=${selectedPrimaryGroup}`
            : `?selected_primary_group_id=${selectedPrimaryGroup}`;
    }

    const fullEndpoint = `${endpoint}${selectedPrimaryGroupQuery}`;

    // Check cache first for GET requests (unless skipCache is true)
    if (method === 'GET' && !skipCache) {
        const cachedData = requestCache.get(fullEndpoint, method, formattedData);

        if (cachedData) {
            return cachedData;
        }

        // Check for pending request
        const pendingRequest = requestCache.getPendingRequest(fullEndpoint, method, formattedData);

        if (pendingRequest) {
            return pendingRequest;
        }
    }

    const request: Request = {
        url: fullEndpoint,
        method,
        data: formattedData,
        headers: {
            'Content-Type': contentType,
            Accept: APP_JSON
        },
        onUploadProgress
    };

    if (unAuth) {
        request.headers.unAuth = 'true';
    }

    if (token) {
        request.headers.customToken = token;
    }

    if (isPublic) {
        request.headers.isPublic = 'true';
    }

    // startTime = new Date().getTime();
    // console.log('--------------------------------');
    // console.log('endpoint:', endpoint);
    // console.log('startTime:', startTime);

    if (method === 'GET') delete request.data;

    if (unAuth) delete request.headers.Authorization;

    if (isHeaderSecretKeys) {
        delete request.headers.secretKey;
        endpoint.concat(`?secretKey${getConfig().API_KEY!}`);
    }

    if (isConvertToPdf) {
        request.headers = {
            ...request.headers,
            Accept: APP_PDF,
            responseType: 'blob'
        };
    }

    let requestData: any;

    if (isObject(request.data)) {
        requestData = { ...request.data };

        if ('image' in requestData) {
            delete requestData.image;
        }

        if ('feature_image' in requestData) {
            delete requestData.feature_image;
        }

        if ('coverPhoto' in requestData) {
            delete requestData.coverPhoto;
        }

        if ('htmlContent' in requestData) {
            delete requestData.htmlContent;
        }

        if ('file' in requestData) {
            delete requestData.file;
        }

        if ('voice_note' in requestData) {
            delete requestData.voice_note;
        }

        if ('about' in requestData) {
            delete requestData.about;
        }

        if ('purpose' in requestData) {
            delete requestData.purpose;
        }
    }

    try {
        Storage.setItem(
            'apiRequest',
            JSON.stringify({
                ...request,
                data: requestData || request.data
            })
        );
    } catch {
        Storage.setItem('apiRequest', 'Unable to set request data in local storage');
    }

    // Create the API request promise
    const apiPromise = (async () => {
        try {
            const response = await API(request);

            if (response?.status === 200) {
                // Cache successful GET responses (unless skipCache is true)
                if (method === 'GET' && !skipCache) {
                    requestCache.set(fullEndpoint, method, formattedData, response.data);
                }

                return response.data;
            } else {
                const error = new Error(response.statusText);

                throw { ...error, response };
            }
        } finally {
            // Clear pending request
            if (method === 'GET' && !skipCache) {
                requestCache.clearPendingRequest(fullEndpoint, method, formattedData);
            }
        }
    })();

    // For GET requests, store the promise to prevent duplicate calls (unless skipCache is true)
    if (method === 'GET' && !skipCache) {
        requestCache.setPendingRequest(fullEndpoint, method, formattedData, apiPromise);
    }

    return apiPromise;
}

export async function REFRESH_TOKEN(config: any) {
    try {
        const { REFRESH_TOKEN } = getConfig();

        let t = ''; let rt = '';

        if (!REFRESH_TOKEN) {
            const { response } = await onGetRefreshToken();

            if (response?.refresh_token) {
                const { token, refresh_token } = await onRefreshToken(response?.refresh_token);

                t = token;
                rt = refresh_token;
            }
        } else {
            const { token, refresh_token } = await onRefreshToken(REFRESH_TOKEN);

            t = token;
            rt = refresh_token;
        }

        config.headers.Authorization = `Bearer ${t}`;

        store.dispatch(
            authUser({
                isAuthed: true
            })
        );

        setConfig({
            TOKEN: t,
            REFRESH_TOKEN: rt
        });

        return await API(config);
    } catch (error) {
        // The ordinary refresh could not repair the session. Embedded, that is
        // not the end of the road: the host still holds a valid Military App
        // session and can mint a fresh handoff token, which is the "access
        // token expired" recovery path.
        //
        // Guarded by a per-request marker so a config that has already been
        // through a handoff cannot go round again — a second 401 after a
        // freshly minted session is the server meaning it, and retrying would
        // spend handoff tokens in a loop.
        if (isEmbedded() && !config.isHandoffRetry) {
            const established = await establishSessionFromHost();

            if (established) {
                config.isHandoffRetry = true;
                config.headers.Authorization = `Bearer ${getConfig().TOKEN}`;

                return await API(config);
            }
        }

        return Promise.reject(error);
    }
}

export async function EXTERNAL_API_REQUEST(
    apiUrl: string,
    endpoint: string,
    data?: any,
    headers?: any,
    method?: API_METHOD,
    isJsonForm?: boolean,
    isText?: boolean
) {
    const url = `${apiUrl}${endpoint}${data && !isJsonForm ? `?${serialize(data)}` : ''}`;

    const response = await fetch(url, {
        method: method || 'GET',
        headers: headers || {},
        body: data
    });

    if (response.ok) {
        const data = await (isText ? response.text() : response.json());

        return data;
    } else {
        const error = new Error(response.statusText);

        throw { ...error, response };
    }
}

function welfareService(welfareService: any): boolean {
    throw new Error('Function not implemented.');
}
// export const onCancelRequest = () => {
//     if (
//         source &&
//         !currentEndpoint?.includes('notifications') &&
//         !currentEndpoint?.includes('push-token') &&
//         !currentEndpoint?.includes('create-user') &&
//         !currentEndpoint?.includes('chat/members') &&
//         !currentEndpoint?.includes('my-group-info') &&
//         !currentEndpoint?.includes('subgroup-info')
//     ) {
//         source.cancel('Unmounted');
//     }
// };
